import type { FormEvent, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { IconActionButton } from "@/components/ui/icon-action-button";
import { Input } from "@/components/ui/input";
import { Tooltip } from "@/components/ui/tooltip";
import { FlexLayout, SaltIcons, Text } from "@/design-system/salt";

function ChromeIconButton({
  label,
  children,
  onClick,
  className,
}: {
  label: string;
  children: ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Tooltip content={label} placement="bottom">
      <Button
        aria-label={label}
        className={className}
        onClick={onClick}
        type="button"
        variant="ghost"
      >
        {children}
      </Button>
    </Tooltip>
  );
}

/** Salt-backed sticky header for authenticated application workspaces. */
export function SaltPageChrome({
  title,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  showSearch = true,
  searchPlaceholder = "Search",
  onMenu,
  primaryAction,
  notifications,
  userMenu,
  className,
}: {
  title: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (value: string) => void;
  showSearch?: boolean;
  searchPlaceholder?: string;
  onMenu?: () => void;
  primaryAction?: { label: string; onClick: () => void };
  notifications?: ReactNode;
  userMenu?: ReactNode;
  className?: string;
}) {
  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearchSubmit(searchValue);
  };

  return (
    <header className={className ? `app-chrome-header ${className}` : "app-chrome-header"}>
      <FlexLayout wrap align="center" gap={1}>
        {onMenu ? (
          <ChromeIconButton
            className="app-chrome-menu-button"
            label="Open navigation"
            onClick={onMenu}
          >
            <SaltIcons.MenuIcon aria-hidden="true" />
          </ChromeIconButton>
        ) : null}

        {/* The chrome owns the page's `<h1>`.
            REVERSED 2026-08-18 by Aaron: one title per screen, and it lives in the chrome.
            The note this replaces argued the opposite — that the chrome title is a route
            label and "the page owns the `<h1>`; do not restore one" — and its measurements
            were correct FOR THE APP AS IT THEN WAS: /corporates carried two h1s
            ("Corporates" here, "Corporate Registry" from the page) and
            /corporate/181905/vendors/<id> carried two reading the identical text.

            What changed is not the evidence, it is the arrangement. Duplicate h1s were the
            symptom of two components both claiming the title; the old fix picked the page,
            this one picks the chrome and REMOVES the page's title in the same sweep — see
            the commits touching PageHeader, ProposalPageHeader and the ~20 pages that
            rendered their own. Restoring `as="h1"` without that removal reinstates exactly
            the duplication the old note describes, so the two changes ship together or not
            at all; `page-titles.test.ts` and the per-page heading tests are what hold that.

            `styleAs="h2"` is deliberately KEPT so the reversal is semantic only: Salt's
            `.saltText-h2.saltText` (0,2,0) outweighs `h1.saltText` (0,1,1) in salt-core.css,
            so the type ramp is untouched and only the UA h1 margin would differ. The chrome
            looks identical before and after; it just now says what it always meant. */}
        <Text as="h1" className="app-chrome-title" styleAs="h2">
          {title}
        </Text>

        {showSearch ? (
          <form className="app-chrome-search" onSubmit={handleSearchSubmit} role="search">
            <Input
              aria-label={searchPlaceholder}
              autoComplete="off"
              onChange={(event) => onSearchChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape" && searchValue) {
                  onSearchChange("");
                }
              }}
              placeholder={searchPlaceholder}
              type="search"
              value={searchValue}
            />
          </form>
        ) : null}

        <FlexLayout align="center" gap={1} style={{ flexShrink: 0 }}>
          {notifications}
          {/* The chrome action is deliberately NOT accented. It lives in the shell, so it renders
              on every dashboard route — and its destination ("Create corporate") has nothing to do
              with most of them. Accenting it meant the single accented button on screen almost
              always belonged to a different page, while the page's own action was forced to ghost
              by PageActionsBar. DR-06 allows exactly one accented action per view, and it belongs
              to the page, not the chrome. */}
          {/* Icon-only per DR-32, via the vehicle DR-32 names.

              This action was ALREADY icon-only at mobile before DR-32 landed — but by
              `.app-chrome-action-label { display: none }`, which is precisely the mechanism
              DR-32 forbids. That CSS was itself a real fix, for a real measured defect, and
              the history is worth keeping: at 390x844 under Salt's TOUCH density
              (`--salt-size-base: 44px`) this cluster was 404px wide inside a 350px header
              row — bell 48 + "Create corporate" 222 + user menu 102. The cluster carries
              `flexShrink: 0` and the header sets no `overflow-x`, and a single flex item
              wider than its line cannot wrap away its OWN overflow, so 35px escaped to the
              document on 12 of the 13 platform routes and sliced the user-menu avatar at
              the right edge. Removing the ~174px label is what bought the fit.

              What the CSS approach cost, MEASURED at 390x844 (salt-density-mobile,
              pointer:coarse true): the button rendered 48px with its label span at
              `display: none, width 0`. The accessible NAME was never lost — `aria-label`
              supplied it independently of the hidden span, so DR-32's stated failure mode
              was not actually present here. What WAS lost is the TOOLTIP. `ChromeIconButton`
              above wraps the menu button in one; this button had none, so at phone widths a
              sighted user had no way to recover the word at all. DR-32 non-negotiable #1 and
              DR-06 both require it.

              `IconActionButton` supplies all three: Tooltip, sr-only label (moved to where
              assistive tech reads it rather than deleted), and `minHeight`/`minWidth` from
              `--salt-size-base` — so the 44px touch target comes from the density token
              instead of a hard-coded pixel. It renders at the same 48px, so the header stays
              140px; MEASURED, this change recovers no vertical space and was never expected
              to. See docs/audit/header-system/DR-32-CHROME-ICON-ONLY.md.

              `flexShrink: 0` on the cluster is deliberately KEPT. Dropping it does not fix
              the overflow on its own — flex items default to `min-width: auto` and refuse to
              shrink below their content — and it would let the cluster squash at widths
              where it currently fits.

              Guarded by `SaltPageChrome.test.tsx` ("gives the chrome action a tooltip and a
              token touch target"). jsdom has no layout engine, so the pixel numbers above
              are browser-measured, not asserted there — but `minHeight`/`minWidth` ARE
              assertable, because cssstyle preserves `var()` for those two specifically. */}
          {primaryAction ? (
            <IconActionButton
              icon={SaltIcons.AddIcon}
              label={primaryAction.label}
              onClick={primaryAction.onClick}
              type="button"
              variant="outline"
            />
          ) : null}
          {userMenu}
        </FlexLayout>
      </FlexLayout>
    </header>
  );
}
